import { Inject, Injectable } from '@angular/core';
import { API_URL, CAPTCHA_CONFIG, CaptchaConfig } from '../app.config';

/**
 * Drives the self-hosted ALTCHA proof-of-work widget (EU-based, open source,
 * free, no cookies) that replaces Google reCAPTCHA.
 *
 * The widget script is loaded once from `config.widget_url` (served from our own
 * assets, no third-party CDN at runtime). `execute()` creates a fresh hidden
 * <altcha-widget> element pointing at the challenge URL, solves the
 * proof-of-work and resolves with the Base64 payload that the backend verifies.
 *
 * The challenge URL defaults to `<api_url>/captcha/challenge` — the backend
 * issues challenges itself, so the fetch stays next to the API (same origin in
 * production; in dev the backend's CORS filter allows the dev origin). Set
 * `challenge_url` in the environment only to use an external ALTCHA server.
 */
@Injectable({
  providedIn: 'root'
})
export class CaptchaService {
  private scriptPromise: Promise<void> | null = null;

  constructor(@Inject(CAPTCHA_CONFIG) private config: CaptchaConfig,
    @Inject(API_URL) private apiUrl: string) {
  }

  private get challengeUrl(): string {
    return this.config.challenge_url || `${this.apiUrl}/captcha/challenge`;
  }

  /**
   * @param action logical action name (kept for parity with the old reCAPTCHA
   *               service; ALTCHA has no per-action tokens)
   * @returns the ALTCHA payload to send as the `token` field, or '' when disabled
   */
  execute(action: string): Promise<string> {
    if (!this.config.enabled) {
      return Promise.resolve('');
    }
    return this.loadWidget().then(() => this.solve());
  }

  private loadWidget(): Promise<void> {
    if (!this.scriptPromise) {
      this.scriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[src="${this.config.widget_url}"]`);
        if (existing) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = this.config.widget_url;
        script.async = true;
        script.defer = true;
        script.type = 'module';
        script.onload = () => {
          // module scripts register the custom element during evaluation;
          // guard with a timeout so a blocked evaluation cannot hang forever
          if (customElements.get('altcha-widget')) {
            resolve();
            return;
          }
          const timeout = setTimeout(() => reject(new Error('Captcha widget did not register')), 10_000);
          customElements.whenDefined('altcha-widget').then(() => {
            clearTimeout(timeout);
            resolve();
          }, reject);
        };
        script.onerror = () => reject(new Error('Failed to load the captcha widget'));
        document.head.appendChild(script);
      });
    }
    return this.scriptPromise;
  }

  private solve(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const widget: any = document.createElement('altcha-widget');
      widget.setAttribute('challengeurl', this.challengeUrl);
      widget.setAttribute('hidefooter', '');
      widget.style.display = 'none';

      let settled = false;
      let timer: any = null;
      let initTimer: any = null;
      let poll: any = null;
      const cleanup = () => {
        clearTimeout(timer);
        clearTimeout(initTimer);
        clearInterval(poll);
        widget.remove();
      };
      const finish = (fn: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        try {
          fn();
        } finally {
          cleanup();
        }
      };
      // never hang forever: bail out after 90s even if the challenge server is unreachable
      timer = setTimeout(() => finish(() => reject(new Error('Captcha timed out'))), 90_000);

      // the widget dispatches 'statechange' with detail { state, payload }
      widget.addEventListener('statechange', (e: any) => {
        const state = e.detail?.state;
        if (state === 'verified') {
          finish(() => resolve(e.detail?.payload ?? widget.value));
        } else if (state === 'error') {
          finish(() => reject(new Error(e.detail?.error || 'Captcha verification failed')));
        }
      });

      document.body.appendChild(widget);
      // verify()/execute() are prototype getters backed by the inner component,
      // which only exists after the element has been connected and mounted —
      // so poll until the method appears, then trigger verification explicitly
      // (ALTCHA v2: verify(), v1: execute())
      initTimer = setTimeout(() => finish(() => reject(new Error('Captcha widget did not initialize'))), 10_000);
      poll = setInterval(() => {
        const starter = typeof widget.verify === 'function' ? widget.verify
          : typeof widget.execute === 'function' ? widget.execute
            : null;
        if (!starter) {
          return;
        }
        clearInterval(poll);
        clearTimeout(initTimer);
        try {
          const result = starter.call(widget);
          if (result && typeof result.then === 'function') {
            // v1 execute() resolves with the payload; v2 verify() resolves void and
            // reports through the statechange event handled above
            result.then((payload: string) => {
              if (payload) {
                finish(() => resolve(payload));
              }
            }).catch((err: any) => finish(() => reject(err)));
          }
        } catch (e) {
          finish(() => reject(e));
        }
      }, 50);
    });
  }
}
